import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Save, Loader2 } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { api, fileUrl } from "../../lib/api";

const PartnerRow = ({ p, onSaved }) => {
  const { t } = useLang();
  const x = t.adminX;
  const [v, setV] = useState({ name: p.name, link: p.link || "", order: p.order });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    try { await api.patch(`/admin/partners/${p.id}`, { ...v, order: Number(v.order) || 0 }); toast.success(t.admin.updated); onSaved(); }
    catch { toast.error(t.errors.generic); }
  };
  const remove = async () => {
    if (!window.confirm(x.confirmDelPartner(p.name))) return;
    try { await api.delete(`/admin/partners/${p.id}`); onSaved(); } catch { toast.error(t.errors.generic); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try { await api.post(`/admin/partners/${p.id}/logo`, form); toast.success(t.admin.updated); onSaved(); }
    catch (err) { toast.error(err?.response?.data?.detail || t.errors.generic); }
    setBusy(false);
    e.target.value = "";
  };

  return (
    <tr data-testid={`admin-partner-row-${p.id}`}>
      <td><div className="partner-logo-cell">{p.logo_url ? <img src={fileUrl(p.logo_url)} alt={p.name} /> : x.noLogo}</div></td>
      <td><input className="f-input" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} aria-label={x.partnerName} data-testid={`admin-partner-name-${p.id}`} /></td>
      <td><input className="f-input" value={v.link} placeholder="https://" onChange={(e) => setV({ ...v, link: e.target.value })} aria-label={x.partnerLink} /></td>
      <td style={{ width: 90 }}><input className="f-input" type="number" min="0" value={v.order} onChange={(e) => setV({ ...v, order: e.target.value })} aria-label={x.partnerOrder} /></td>
      <td>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <label className="btn btn-ghost btn-sm file-btn" data-testid={`admin-partner-upload-${p.id}`}>
            {busy ? <Loader2 className="spin" aria-hidden="true" /> : <Upload aria-hidden="true" />}{busy ? x.uploading : x.upload}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={upload} aria-label={x.partnerLogo} data-testid={`admin-partner-file-${p.id}`} />
          </label>
          <button type="button" className="btn btn-ghost btn-sm" onClick={save} data-testid={`admin-partner-save-${p.id}`}><Save aria-hidden="true" />{x.save}</button>
          <button type="button" className="btn btn-danger btn-sm" onClick={remove} data-testid={`admin-partner-delete-${p.id}`}><Trash2 aria-hidden="true" />{t.admin.del}</button>
        </div>
      </td>
    </tr>
  );
};

export const AdminPartners = () => {
  const { t } = useLang();
  const x = t.adminX;
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", link: "", order: "" });
  const load = useCallback(async () => setRows((await api.get("/admin/partners")).data), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error(t.errors.min(2));
    if (form.link && !/^https:\/\/\S+$/.test(form.link)) return toast.error(x.partnerLink);
    try {
      await api.post("/admin/partners", { name: form.name.trim(), link: form.link || null, order: Number(form.order) || rows.length + 1 });
      setForm({ name: "", link: "", order: "" });
      load();
    } catch { toast.error(t.errors.generic); }
  };

  return (
    <div data-testid="admin-partners-panel">
      <form onSubmit={add} className="admin-form-row" noValidate data-testid="admin-partner-form">
        <div className="f-field"><label htmlFor="partner-name">{x.partnerName}</label><input id="partner-name" className="f-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-partner-name-input" /></div>
        <div className="f-field"><label htmlFor="partner-link">{x.partnerLink}</label><input id="partner-link" className="f-input" placeholder="https://" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="admin-partner-link-input" /></div>
        <div className="f-field"><label htmlFor="partner-order">{x.partnerOrder}</label><input id="partner-order" type="number" min="0" className="f-input" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="admin-partner-order-input" /></div>
        <button type="submit" className="btn btn-primary" data-testid="admin-partner-add-btn"><Plus aria-hidden="true" />{x.addPartner}</button>
      </form>
      <p className="f-note" style={{ margin: "14px 0 0" }}>{x.partnerLogo}</p>
      {rows.length === 0 ? <p className="empty" data-testid="admin-partners-empty">{x.partnersEmpty}</p> : (
        <div className="table-wrap"><table className="data-table" style={{ minWidth: 920 }} data-testid="admin-partners-table">
          <thead><tr><th>Logo</th><th>{x.partnerName}</th><th>{x.partnerLink}</th><th>{x.partnerOrder}</th><th>{t.admin.h.actions}</th></tr></thead>
          <tbody>{rows.map((p) => <PartnerRow key={`${p.id}-${p.logo_url}`} p={p} onSaved={load} />)}</tbody>
        </table></div>
      )}
    </div>
  );
};
